/* „Do przejrzenia" as an inbox — the queue on the left, the open submission on the right, a foot
 * under both (owner, 4 November 2026; the canvas page „Finał", row F3).
 *
 *   card       fills the tab's height; the list and the pane scroll inside it, never the page
 *   list       400px, a header band („4 czeka na przejrzenie", „następne J K"), then the rows
 *   row        avatar, name 14/600 with the form's short kind 13/400, one summary line, the day
 *              on the right with the lateness under it; the open row on --bg-chip with a 2px bar
 *   pane       head (name 18/600 · form 400, the meta line, the four acts), then the chart strip
 *              and the report
 *   foot       12px --text-3, the reviewed-items sentence left and the window right
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

.tab {
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
}

.card {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-card);
}

.inbox {
  display: grid;
  flex: 1;
  grid-template-columns: var(--w-report-inbox-list) minmax(0, 1fr);
  min-height: 0;
}

/* ── the queue ─────────────────────────────────────────────────────────────────────────────── */

.listColumn {
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: var(--border-hairline) solid var(--border);
}

.listHead {
  display: flex;
  flex: none;
  gap: var(--space-3);
  align-items: center;
  justify-content: space-between;
  height: var(--h-report-tabbar);
  padding: var(--space-0) var(--space-20px);
  font-size: var(--type-control);
  color: var(--text-2);
}

.listCount {
  font-variant-numeric: tabular-nums;
}

.listKeys {
  display: inline-flex;
  gap: var(--space-1);
  align-items: center;
}

.listKbd {
  color: var(--text-3);
}

.list {
  flex: 1;
  min-height: 0;
  margin: var(--space-0);
  padding: var(--space-0);
  overflow-y: auto;
  list-style: none;
}

.row {
  position: relative;
  border-top: var(--border-hairline) solid var(--border);
}

/* The open submission: a fill and a 2px accent bar, beside `aria-current` on the button — the
 * state is never the colour alone. */
.rowOn {
  background: var(--bg-chip);
}

.rowOn::before {
  position: absolute;
  inset: var(--space-10px) auto var(--space-10px) var(--space-0);
  width: var(--border-thick);
  content: '';
  background: var(--accent);
  border-radius: var(--radius-hair);
}

.rowButton {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: var(--space-3);
  align-items: start;
  box-sizing: border-box;
  width: 100%;
  min-height: var(--h-report-queue-row);
  padding: var(--space-14px) var(--space-20px);
  font: inherit;
  color: inherit;
  text-align: start;
  cursor: pointer;
  background: none;
  border: 0;

  /* The one thing a surface may say about `shared/ui/AvatarChip`. */
  --sz-avatar: var(--sz-report-pane-face);
}

.rowButton:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

.row:not(.rowOn) .rowButton:hover {
  background: var(--hover-tint);
}

.rowText {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: 0;
}

.rowTop {
  display: flex;
  gap: var(--space-6px);
  align-items: baseline;
  min-width: 0;
}

.rowName {
  overflow: hidden;
  font-size: var(--type-body);
  font-weight: var(--fw-semibold);
  line-height: var(--type-body-lh);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rowForm {
  flex: none;
  font-size: var(--type-control);
  font-weight: var(--fw-regular);
  color: var(--text-2);
}

.rowSummary {
  overflow: hidden;
  font-size: var(--type-control);
  line-height: var(--type-control-lh);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.rowWhen {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: var(--type-label);
  line-height: var(--type-body-lh);
  color: var(--text-3);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.rowDay {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
}

.rowLate {
  line-height: var(--type-label-lh);
  color: var(--text-2);
}

/* An on-time submission's dot (owner, 4 November 2026). Words carry it for a listener. */
.pip {
  flex: none;
  width: 7px;
  height: 7px;
  background: var(--accent);
  border-radius: var(--r-circle);
}

/* ── the pane ──────────────────────────────────────────────────────────────────────────────── */

.pane {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.paneHead {
  display: flex;
  flex: none;
  gap: var(--space-4);
  align-items: flex-start;
  justify-content: space-between;
  min-height: var(--h-report-inbox-head);
  padding: var(--space-22px) var(--space-7) var(--space-5);
  border-bottom: var(--border-hairline) solid var(--border);
}

.paneHeadText {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.paneTitle {
  margin: var(--space-0);
  overflow: hidden;
  font-size: var(--type-title-2);
  font-weight: var(--fw-semibold);
  line-height: var(--type-title-2-lh);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paneForm {
  font-weight: var(--fw-regular);
  color: var(--text-2);
}

.paneMeta {
  margin: var(--space-0);
  font-size: var(--type-control);
  line-height: var(--type-control-lh);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

.paneActions {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
}

.paneStep,
.paneGhost {
  justify-content: center;
  height: var(--h-control);
  font-size: var(--type-control);
  font-weight: var(--fw-semibold);
  text-decoration: none;
  background: transparent;
  border-radius: var(--r-pill);
}

.paneStep {
  width: var(--h-control);
  padding: var(--space-0);
}

.paneGhost {
  padding: var(--space-0) var(--space-3);
}

.paneStep:not([aria-disabled='true']):hover,
.paneGhost:hover {
  background: var(--hover-tint);
}

.panePrimary {
  gap: var(--space-2);
  height: var(--h-control);
  padding: var(--space-0) var(--space-2) var(--space-0) var(--space-4);
  font-size: var(--type-control);
  border-radius: var(--r-pill);
}

.panePrimaryIcon {
  width: var(--icon-xs);
  height: var(--icon-xs);
}

/* The keycap inside the ember pill takes the pill's own ink. */
.paneKbd {
  border-color: var(--on-accent-grad);
}

.paneBody {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-6);
  min-height: 0;
  padding: var(--space-22px) var(--space-7);
  overflow-y: auto;
}

.paneReport {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.paneReserve {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-height: var(--h-report-queue-min);
}

.paneState {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  align-items: center;
  justify-content: center;
  min-height: var(--h-report-queue-min);
}

/* ── the foot, and the card's other states ─────────────────────────────────────────────────── */

.foot {
  display: flex;
  flex: none;
  gap: var(--space-4);
  align-items: center;
  justify-content: space-between;
  height: var(--h-report-tabbar);
  padding: var(--space-0) var(--space-7);
  font-size: var(--type-label);
  color: var(--text-3);
  border-top: var(--border-hairline) solid var(--border);
  font-variant-numeric: tabular-nums;
}

/* A mark that did not reach the backend. The one place the foot speaks up. */
.failed {
  font-weight: var(--fw-semibold);
  color: var(--danger-text-strong);
}

.state {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-4);
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: var(--space-7);
  text-align: center;
}

/* ── the wait ──────────────────────────────────────────────────────────────────────────────── */

.skelRow {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  box-sizing: border-box;
  min-height: var(--h-report-queue-row);
  padding: var(--space-14px) var(--space-20px);
  border-top: var(--border-hairline) solid var(--border);
}

.skelFace {
  flex: none;
  width: var(--sz-report-pane-face);
  height: var(--sz-report-pane-face);
  border-radius: var(--r-circle);
}

.skelText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
}

.skelName {
  width: 52%;
  height: 12px;
}

.skelSummary {
  width: 78%;
  height: 10px;
}

.skelWhen {
  flex: none;
  width: 40px;
  height: 10px;
}

.skelTitle {
  width: 280px;
  height: 16px;
}

.skelBlock {
  height: 72px;
}
